import React, { useState, useEffect } from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import ReactJson from "react-json-view";
import useNear from "../hooks/nearHook";
import useSbt from "../hooks/sbtHook";
import useApi from "../hooks/apiHook";
import SbtMetadataTable from "../components/SbtMetadataTable";
import AddBoxIcon from "@mui/icons-material/AddBox";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import IconButton from "@mui/material/IconButton";
import SbtSelector from "../components/SbtSelector";
import AccountField from "../components/AccountField";
import useTokenRouting from "../hooks/tokenRoutingHook";
import { useNavigate } from "react-router-dom";
import empty from "json-schema-empty";

const DEFAULT_TOKEN = {
  schema: null,
  body: {},
  reference: "",
  key_needed_for_access: "FunctionCall",
  children: [],
};

const SbtTokenEditor = ({
  token,
  setToken,
  disabled,
  schemas,
  onDelete = null,
}) => {
  const setSchema = (schemaName) => {
    const schema =
      schemaName == null ? null : schemas.find((s) => s.shortId === schemaName);
    setToken((token) => ({ ...token, schema: schema ? schema["$id"] : null }));
    setToken((token) => ({
      ...token,
      body: schema == null ? {} : empty(schema),
    }));
  };

  return (
    <>
      <Grid container>
        <Grid item xs={1}>
          {onDelete && (
            <IconButton onClick={onDelete}>
              <IndeterminateCheckBoxIcon />
            </IconButton>
          )}
        </Grid>
        <Grid item xs={onDelete ? 11 : 12}>
          <FormControl fullWidth disabled={disabled}>
            <InputLabel id="schema-select">Schema</InputLabel>
            <Select
              labelId="schema-select"
              value={schemas.find(s => s["$id"] === token.schema)?.shortId || "None"}
              label="Schema"
              onChange={(e) =>
                setSchema(e.target.value === "None" ? null : e.target.value)
              }
            >
              {schemas.map((schema) => (
                <MenuItem value={schema.shortId} key={schema.name}>
                  {schema.shortId}
                </MenuItem>
              ))}
              <MenuItem value={"None"}>None</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <ReactJson
        src={token.body || {}}
        name={"body"}
        style={{
          overflow: "auto",
          minHeight: 200,
          height: 400,
          marginLeft: 8,
          marginTop: 3,
        }}
        enableClipboard={false}
        onEdit={
          !disabled &&
          ((e) => setToken((token) => ({ ...token, body: e.updated_src })))
        }
        onAdd={
          !disabled &&
          ((e) => setToken((token) => ({ ...token, body: e.updated_src })))
        }
        onDelete={
          !disabled &&
          ((e) => setToken((token) => ({ ...token, body: e.updated_src })))
        }
        disabled={disabled}
      />
      <FormControl
        fullWidth
        disabled={disabled}
        required
        style={{ marginBottom: 5 }}
      >
        <InputLabel id="knfa-select">Key needed for access</InputLabel>
        <Select
          labelId="knfa-select"
          value={token.key_needed_for_access}
          label="Key needed for access"
          onChange={(e) =>
            setToken((token) => ({
              ...token,
              key_needed_for_access: e.target.value,
            }))
          }
        >
          <MenuItem value={"FunctionCall"}>FunctionCall</MenuItem>
          <MenuItem value={"FullAccess"}>FullAccess</MenuItem>
          <MenuItem value={"None"}>None</MenuItem>
        </Select>
      </FormControl>
      <TextField
        onChange={(e) =>
          setToken((token) => ({ ...token, reference: e.target.value }))
        }
        value={token.reference || ""}
        label="Reference"
        variant="outlined"
        fullWidth
        disabled={disabled}
      />
      <Grid container sx={{ marginTop: 1 }}>
        <Grid item xs={1}>
          <IconButton
            onClick={() =>
              setToken((token) => ({
                ...token,
                children: [...(token.children || []), DEFAULT_TOKEN],
              }))
            }
            disabled={disabled}
          >
            <AddBoxIcon />
          </IconButton>
        </Grid>
        <Grid
          item
          xs={11}
          style={{ borderLeft: "2px solid gray", paddingLeft: 5 }}
        >
          <Grid container spacing={1}>
            {(token.children || []).map((childToken, i) => (
              <Grid item xs={12}>
                <SbtTokenEditor
                  token={childToken}
                  setToken={(tokenSetter) =>
                    setToken((token) => ({
                      ...token,
                      children: [
                        ...token.children.map((_t, _i) =>
                          _i === i ? tokenSetter(_t) : _t
                        ),
                      ],
                    }))
                  }
                  onDelete={() =>
                    setToken((token) => ({
                      ...token,
                      children: [
                        ...token.children.filter((_t, _i) => _i !== i),
                      ],
                    }))
                  }
                  disabled={disabled}
                  schemas={schemas}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

const CreateSbtDashboard = ({}) => {
  const near = useNear();
  const sbt = useSbt();
  const apiFactory = useApi();
  // const [contractId, setContractId] = useState(""); // TODO: Store history for convinience; resume from last used
  const [sbtToken, setSbtToken] = useState(DEFAULT_TOKEN);
  const [loading, setLoading] = useState(false);
  const [schemas, setSchemas] = useState([]);
  const navigate = useNavigate();

  // const [ownerId, setOwnerId] = useState(near.account.account);

  const [sbtMetadata, setSbtMetadata] = useState({});
  const {
    contractId,
    accountId: ownerId,
    setContractId,
    setAccountId: setOwnerId,
  } = useTokenRouting();

  useEffect(() => {
    if (!near.account.account) {
      return;
    }
    sbt.getContractMetadata(contractId).then((result) => {
      setSbtMetadata(result || {});
      if (!result?.base_uri) {
        setSchemas([]);
        return;
      }
      const api = apiFactory.getApi(result.base_uri);
      api.getSchemas().then((result) =>
        setSchemas(
          result.map((s) => ({
            ...s,
            shortId: s["$id"]
              .split("/")
              .slice(-1)[0]
              .replace(/\.schema\.json$/g, ""),
          }))
        )
      );
    });
    setOwnerId(near.account.account);
    setSbtToken(DEFAULT_TOKEN);
  }, [contractId, near.account.account]);

  useEffect(() => {
    setOwnerId(near.account.account);
  }, [near.account.account]);

  const createToken = () => {
    if (!sbtMetadata || !ownerId) {
      return;
    }
    setLoading(true);
    const api = apiFactory.getApi(sbtMetadata.base_uri);
    api.createTokens(ownerId, [sbtToken]).then((res) => {
      setSbtToken(DEFAULT_TOKEN);
      setLoading(false);
      const tokenCreated = res[0];
      navigate(`/sbt/${contractId}/${ownerId}/${tokenCreated.id}`);
      console.log();
    }); // TODO: Open snackbar on success
  };

  const editorEnabled = !!sbtMetadata.spec && !loading;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  height: 420,
                }}
              >
                <SbtSelector
                  sbtContractId={contractId}
                  setSbtContractId={setContractId}
                />
                <SbtMetadataTable sbtMetadata={sbtMetadata} />
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  height: 380,
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12}></Grid>
                  <Grid item xs={12}>
                    <AccountField
                      accountId={ownerId}
                      setAccountId={setOwnerId}
                      label="Owner"
                      disabled={!editorEnabled}
                    />
                  </Grid>
                  <Grid item sx={{ height: 240 }}></Grid>
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={createToken}
                      disabled={!editorEnabled}
                    >
                      Create
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              height: 825,
              overflow: "auto",
            }}
          >
            <SbtTokenEditor
              token={sbtToken}
              setToken={setSbtToken}
              disabled={!editorEnabled}
              schemas={schemas}
            />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CreateSbtDashboard;
